import React, { memo, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Modal, Toast } from '@ow/comps'
import { WebBasicForm } from '@ow/ditto'
import { fieldFilter } from '@ow/ditto-core'
import { pick } from 'lodash-es'
import { useFetchRemoteData, useTbViewStore } from '@vt/hooks'

const FIELD_SEL_SLUG = '__fls'

const filterFiled = (fields) => {
  return fields.filter((field) => fieldFilter(field) && !field.hiddenWhenCreate)
}

const genLogicSpec = (value) => {
  return {
    logicType: 'and',
    conditions: [
      {
        slug: FIELD_SEL_SLUG,
        op: 'containArray',
        value: [value],
      },
    ],
    results: {
      opts: { required: true, hiddenWhenCreate: false },
    },
  }
}

const genLogicedFields = (_fields) => {
  const fields = filterFiled(_fields).map((x) => {
    return { ...x, logicSpec: genLogicSpec(x.slug), hiddenWhenCreate: true }
  })

  fields.unshift({
    slug: FIELD_SEL_SLUG,
    title: '选择要批量编辑的字段1',
    description: '通过批量编辑可快速统一修改相同字段的数据内容',
    selects: fields.map((x) => {
      return {
        title: x.title,
        value: x.title,
        slug: x.slug,
        type: x.type,
        needInput: false,
      }
    }),
    required: true,
    readonly: false,
    multiple: false,
    type: 'dropSelect',
  })

  return fields
}

const FooterRender = ({ close, onClickSubmit, loading }) => {
  return (
    <div className="flex gap-2">
      <Button type="secondary" opacityType="sm" onClick={close}>
        取消
      </Button>
      <Button
        type="primary"
        loading={loading}
        onClick={() => {
          onClickSubmit(close)
        }}
      >
        确定
      </Button>
    </div>
  )
}

const Editor = ({ children }) => {
  const location = useLocation()

  const [loading, setLoading] = useState(false)
  const [initForm, setInitForm] = useState()

  const modalRef = useRef(null)
  const formRef = useRef(null)
  const autoOpenRef = useRef(false)

  const { fields, selectedIds, setSelectedIds, batchUpdateTbData, tbData } =
    useTbViewStore((state) =>
      pick(state, [
        'tbData',
        'fields',
        'batchUpdateTbData',
        'setSelectedIds',
        'selectedIds',
      ])
    )

  const cloumns = [...fields]
  const logicedFields = genLogicedFields(cloumns)
  const fetchRemoteData = useFetchRemoteData()

  const onClickSubmit = (afterSubmit) => {
    if (formRef && formRef.current && selectedIds.length > 0) {
      const props = {}
      const obj = formRef.current.getValues()
      const { __fls } = obj
      if (__fls && __fls.length > 0) {
        props.slugs = __fls
        props.datas = pick(obj, __fls[0])

        setLoading(true)
        batchUpdateTbData(props, selectedIds)
          .then(() => {
            afterSubmit()
            Toast.success({
              title: '操作成功',
              description: '更新成功！',
            })
          })
          .catch((err) => {
            Toast.error({
              title: '操作失败',
              description: err.message,
            })
          })
          .finally(() => {
            setLoading(false)
          })
      }
    }
  }

  useEffect(() => {
    if (
      location.state?.actionType === 'batchEdit' && // 需要自动打开编辑表单
      tbData.length && // 获取到数据
      !autoOpenRef.current // 只弹出一次
    ) {
      const { slug, ids } = location.state.payload

      setSelectedIds(ids)
      modalRef.current?.open()
      setInitForm({ [FIELD_SEL_SLUG]: [slug] })
      autoOpenRef.current = true
    }
  }, [tbData])

  return (
    <Modal
      ref={modalRef}
      title="批量编辑"
      nested
      size="lg"
      showCloseButton
      contentStyle={{ height: '30rem' }}
      trigger={selectedIds.length && children}
      footer={({ close }) => FooterRender({ close, loading, onClickSubmit })}
    >
      <div className="h-full">
        <WebBasicForm
          data={initForm}
          fields={logicedFields}
          onFetchRemoteData={fetchRemoteData}
          style={{ height: '100%' }}
          ref={formRef}
        />
      </div>
    </Modal>
  )
}

export default memo(Editor)
